import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createJSONStorage, persist } from 'zustand/middleware';
import { JobApplication, JobApplicationStatus } from '../generated/gql-types';
import { jobApplicationActions } from './jobApplication.action';
import { removeOne, upsertOne } from '../util/zustand.util';
import { acceptJobApplicationEffect } from './jobApplication.effect';
import type { AssignmentRateOverrides } from '../job/assignmentRateOverrides.type';

interface JobApplicationStore {
  jobApplications: JobApplication[];
  isLoading: boolean;
  filterStatus: JobApplicationStatus[] | null;
  upsertJobApplications: (applications: JobApplication[]) => void;
  setFilterStatus: (status: JobApplicationStatus[] | null) => void;
  setJobApplications: (applications: JobApplication[]) => void;
  fetchJobApplicationsForUser: (userId: string) => Promise<JobApplication[]>;
  fetchJobApplicationsForJob: (jobId: string) => Promise<JobApplication[]>;
  fetchJobApplicationsForClient: (
    clientId: string,
  ) => Promise<JobApplication[]>;
  respondToApplication: (
    jobApplicationId: string,
    itemStatus: JobApplicationStatus,
    rateOverrides?: AssignmentRateOverrides,
  ) => Promise<boolean>;
  getJobApplications: () => JobApplication[];
  getJobApplicationsForUser: (userId: string) => JobApplication[];
  getJobApplicationsForJob: (jobId: string) => JobApplication[];
  getJobApplicationByJobId: (jobId: string) => JobApplication | null;
  getJobApplicationById: (jobApplicationId: string) => JobApplication | null;
  getInProgressJobApplications: () => JobApplication[];
  removeJobApplication: (jobApplicationId: string) => void;
}

// Oldest application date first so earliest applicants are prioritised.
const sortJobApplicationsByCreatedAtAsc = (
  applications: JobApplication[],
): JobApplication[] =>
  [...applications].sort(
    (firstApplication, secondApplication) =>
      parseInt(firstApplication.createdAt, 10) -
      parseInt(secondApplication.createdAt, 10),
  );

const normalisePersistedJobApplicationState = (
  persistedState: unknown,
): Partial<JobApplicationStore> => {
  if (!persistedState || typeof persistedState !== 'object') {
    return {};
  }

  const persistedJobApplicationStore =
    persistedState as Partial<JobApplicationStore>;

  return {
    ...persistedJobApplicationStore,
    jobApplications: sortJobApplicationsByCreatedAtAsc(
      persistedJobApplicationStore.jobApplications ?? [],
    ),
  };
};

export const useJobApplicationStore = create<JobApplicationStore>()(
  persist(
    (set, get) => ({
      //   state
      jobApplications: [],
      isLoading: false,
      filterStatus: null,

      // Helper functions for state management
      upsertJobApplications: (applications: JobApplication[]) => {
        set((state) => ({
          jobApplications: sortJobApplicationsByCreatedAtAsc(
            applications.reduce(
              (acc, app) => upsertOne(acc, app),
              state.jobApplications,
            ),
          ),
        }));
      },

      // setters
      setJobApplications: (applications) => {
        set({
          jobApplications: sortJobApplicationsByCreatedAtAsc(applications),
        });
      },
      setFilterStatus: (status: JobApplicationStatus[] | null) =>
        set({ filterStatus: status }),

      // actions
      fetchJobApplicationsForUser: async (
        userId,
      ): Promise<JobApplication[]> => {
        try {
          set({ isLoading: true });
          const applications =
            await jobApplicationActions.fetchJobApplicationsForUser(userId);
          set({
            jobApplications: sortJobApplicationsByCreatedAtAsc(applications),
          });
          return applications;
        } catch (error) {
          console.error('Error fetching job applications for user: ', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchJobApplicationsForJob: async (jobId) => {
        try {
          set({ isLoading: true });
          const newApplications =
            await jobApplicationActions.fetchJobApplicationsForJob(jobId);
          get().upsertJobApplications(newApplications);
          return newApplications;
        } catch (error) {
          console.error('Error fetching job applications for job:', error);
        } finally {
          set({ isLoading: false });
        }
      },
      fetchJobApplicationsForClient: async (clientId) => {
        try {
          const newApplicationsForClient =
            await jobApplicationActions.fetchJobApplicationsForClient(clientId);
          get().upsertJobApplications(newApplicationsForClient);
          return newApplicationsForClient;
        } catch (error) {
          console.error('Error fetching job applications for client:', error);
        }
      },
      respondToApplication: async (
        jobApplicationId: string,
        itemStatus: JobApplicationStatus,
        rateOverrides?: AssignmentRateOverrides,
      ): Promise<boolean> => {
        try {
          set({ isLoading: true });
          const jobApplication = get().getJobApplicationById(jobApplicationId);
          const jobId = jobApplication?.jobId;

          const success = await jobApplicationActions.respondToApplication(
            jobApplicationId,
            itemStatus,
            rateOverrides,
          );

          if (success) {
            const updatedApplications = get().jobApplications.map((app) =>
              app.id === jobApplicationId ? { ...app, itemStatus } : app,
            );

            set({
              jobApplications:
                sortJobApplicationsByCreatedAtAsc(updatedApplications),
            });

            // If the application was accepted, update store accordingly.
            if (itemStatus === JobApplicationStatus.Accepted && jobId) {
              await acceptJobApplicationEffect(jobId);
            }
            return success;
          }
          set({ isLoading: false });
          return false;
        } catch (error) {
          console.error('Error responding to job application:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      // getters
      getJobApplications: () => get().jobApplications,
      getJobApplicationsForUser: (userId: string): JobApplication[] =>
        get().jobApplications.filter(
          (application: JobApplication) => application.userId === userId,
        ),
      getJobApplicationsForJob: (jobId: string): JobApplication[] =>
        get().jobApplications.filter(
          (application: JobApplication) => application.jobId === jobId,
        ),
      getJobApplicationByJobId: (jobId: string): JobApplication | null =>
        get().jobApplications.find(
          (application: JobApplication) => application.jobId === jobId,
        ) || null,
      getJobApplicationById: (
        jobApplicationId: string,
      ): JobApplication | null =>
        get().jobApplications.find(
          (application: JobApplication) => application.id === jobApplicationId,
        ) || null,
      getInProgressJobApplications: () =>
        get().jobApplications.filter(
          (application: JobApplication) =>
            application.itemStatus !== JobApplicationStatus.Accepted,
        ),
      removeJobApplication: (jobApplicationId: string): void => {
        set((state) => ({
          jobApplications: sortJobApplicationsByCreatedAtAsc(
            removeOne(state.jobApplications, jobApplicationId),
          ),
        }));
      },
    }),
    {
      name: 'job-applications-storage',
      storage: createJSONStorage(() => AsyncStorage),
      version: 1,
      migrate: async (persistedState) =>
        normalisePersistedJobApplicationState(persistedState),
    },
  ),
);
